/**
 * Question Validation — Content Domain
 *
 * Pure validation functions for questions and seed data.
 * No dependencies on loaded question arrays (no circular deps).
 *
 * Created in R38 (DDD Fase 7 — questionLoader god-file split).
 */

import { QuestionCore } from '../types';
import { getQuestionTypeConfig } from './questionTypeRegistry';
import type { SeedQuestion } from './seedConsolidator';

/**
 * Assert that a seed question uses the canonical feedback payload format.
 * Throws if deprecated keys are found.
 */
export function assertCanonicalSeedFeedback(seed: SeedQuestion): void {
  const raw = seed as unknown as Record<string, unknown>;
  const nestedFeedback = (raw.feedback ?? {}) as Record<string, unknown>;
  const deprecatedFound: string[] = [];
  if ('visualAid' in raw) deprecatedFound.push('visualAid');
  if ('explanation' in raw) deprecatedFound.push('explanation');
  if ('explanationPerOption' in raw) deprecatedFound.push('explanationPerOption');
  if ('explanationCorrect' in raw) deprecatedFound.push('explanationCorrect');
  if ('distractorExplanations' in raw) deprecatedFound.push('distractorExplanations');
  if ('stimulusAids' in nestedFeedback) deprecatedFound.push('feedback.stimulusAids');
  if ('supportAids' in nestedFeedback) deprecatedFound.push('feedback.supportAids');
  if ('explanation' in nestedFeedback) deprecatedFound.push('feedback.explanation');
  if ('explanationPerOption' in nestedFeedback) deprecatedFound.push('feedback.explanationPerOption');
  if (deprecatedFound.length > 0) {
    throw new Error(`Non-canonical feedback payload for question ${seed.id}. Deprecated keys: ${deprecatedFound.join(', ')}. Supported contract: feedback.explanationText, feedback.solutionSteps, feedback.distractorExplanations, stimulusAids, supportAids.`);
  }
}

/**
 * CITO Eligibility Check.
 */
export function isCitoEligible(question: QuestionCore): boolean {
  const type = question.questionType;
  const config = getQuestionTypeConfig(type);
  return config.isCitoEligible && !question.context;
}

/**
 * Strict validation — errors for required taxonomy fields.
 */
export function validateQuestion(question: QuestionCore): string[] {
  const errors: string[] = [];
  if (!question.questionType) {
    errors.push(`Question ${question.id} is missing required questionType`);
  } else {
    try {
      getQuestionTypeConfig(question.questionType);
    } catch (error) {
      errors.push(`Question ${question.id} has invalid canonical questionType: ${question.questionType}`);
    }
  }
  if (!question.familyId) {
    errors.push(`Question ${question.id} is missing required familyId`);
  }
  if (!question.skill) {
    errors.push(`Question ${question.id} is missing required skill`);
  }
  if (!question.subSkill) {
    errors.push(`Question ${question.id} is missing required subSkill`);
  }
  if (!question.tier || question.tier < 1 || question.tier > 16) {
    errors.push(`Question ${question.id} has invalid tier: ${question.tier} (valid: 1-16)`);
  }
  if (!question.levelCode) {
    errors.push(`Question ${question.id} is missing required levelCode`);
  }
  if (!question.group || ![6, 7, 8].includes(question.group)) {
    errors.push(`Question ${question.id} has invalid group: ${question.group}`);
  }
  return errors;
}